import React, { memo, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { ValidationError } from 'yup';

import {
	Button,
	GroupSelectItemInterface,
	Modal,
	RadioButton,
	Select,
	SpinnerOverlay } from '@components/common';
import {
	ECheckForFields,
} from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/AddKpiAlertCheckFor/enum';
import KpiThresholdsModal
	from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/KpiThresholdsModal';
import { ETabsIds } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/tabsIds';

import { useThresholds } from '@hooks/useThresholds';

import { isEmpty } from '@utils/common';

import { IThreshold } from '@interfaces/alarms/threshold';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IAddKpiNotificationConditionProps, IKpiAlertSchema } from '../interfaces';
import {
	IGroupSelectCategoryGroup,
	IGroupSelectOptionsKpi,
} from '@components/common/GroupSelectKpi/GroupSelect.types';

import { EVendorsValues } from '@enums/common';
import { EApplyFor, EConditionFields, EThresholdSource } from './enums';

import { hoursOptions, minutesOptions } from '@constants/timeOptions';

import { filterValuesByKeys } from '../utils';

import { conditionSchema } from './schema';

function AddKpiAlertCondition({
	setActiveTab,
	formMethods,
	formState,
	values,
	negativeHandler,
	isEdit,
	positiveBtnText,
	negativeBtnText,
	positiveHandler,
}: IAddKpiNotificationConditionProps) {
	const {
		watch,
		setError,
		register,
		control,
		setValue,
		clearErrors,
		handleSubmit,
	} = formMethods;
	const { errors } = formState;

	const [isDirtyForm, setIsDirtyForm] = useState(false);

	const checkForValues = filterValuesByKeys(values, Object.keys(ECheckForFields));

	const technologies: string[] | null = checkForValues
		? Object.keys(checkForValues).reduce((acc: string[], key: string): string[] => {
			if (isEmpty(checkForValues[key])) return acc;

			if (key.includes('4G') && !acc.includes('T_4G')) acc.push('T_4G');
			else if (key.includes('5G')) acc.push('T_5G');

			return acc;
		}, [])
		: null;

	const [isOpenThresholdModal, setIsOpenThresholdModal] = useState(false);

	const {
		thresholds,
		isLoading: isLoadingThresholds,
	} = useThresholds({
		technologies,
		vendor: values?.vendor?.value,
	});

	const isOranVendor = values?.vendor?.value === EVendorsValues.oran;

	const filterThresholdsByKpiIds = useCallback((thresholds: IThreshold[] = []) => {
		return thresholds.filter((threshold: IThreshold) => {
			return values.kpi.some((kpi: IGroupSelectOptionsKpi) => {
				if (isOranVendor) {
					return (kpi as any).isChecked && (kpi as any).dbId === threshold.kpiId;
				}

				return kpi.categoryGroups.some((categoryGroup: IGroupSelectCategoryGroup): boolean => {
					return categoryGroup.items.some((item: GroupSelectItemInterface) => item.id === threshold.kpiId);
				});
			});
		});
	}, [isOranVendor, values.kpi]);

	useEffect(() => {
		if (isEmpty(thresholds)) return;

		if (values.thresholdSource === EThresholdSource.useDefaultForKPIs) {
			setValue('thresholds', filterThresholdsByKpiIds(thresholds!), { shouldValidate: true, shouldDirty: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.thresholdSource, isEmpty(thresholds)]);

	const thresholdSource = watch(EConditionFields.thresholdSource);
	const applyFor = watch(EConditionFields.applyFor);
	const checkEveryHours = watch(EConditionFields.checkEveryHours);
	const checkEveryMinutes = watch(EConditionFields.checkEveryMinutes);
	const evaluationWindowHours = watch(EConditionFields.evaluationWindowHours);
	const evaluationWindowMinutes = watch(EConditionFields.evaluationWindowMinutes);

	useEffect(() => {
		async function validateFields() {
			const conditionValues = filterValuesByKeys(values, Object.keys(EConditionFields));

			try {
				await conditionSchema.validateAt('checkEveryHours', conditionValues);
				clearErrors('checkEveryHours');

				await conditionSchema.validateAt('checkEveryMinutes', conditionValues);
				clearErrors('checkEveryMinutes');

				await conditionSchema.validateAt('evaluationWindowHours', conditionValues);
				clearErrors('evaluationWindowHours');

				await conditionSchema.validateAt('evaluationWindowMinutes', conditionValues);
				clearErrors('evaluationWindowMinutes');
			} catch (e) {
				return;
			}
		}

		void validateFields();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		checkEveryHours?.value,
		checkEveryMinutes?.value,
		evaluationWindowHours?.value,
		evaluationWindowMinutes?.value,
	]);

	const conditionValues = filterValuesByKeys(values, Object.keys(EConditionFields));

	const onHandleNext = useCallback(async () => {
		if (isEdit) {
			return handleSubmit(positiveHandler)();
		}

		try {
			await conditionSchema.validate(conditionValues, { abortEarly: false });

			setActiveTab(ETabsIds.schedule);
		} catch (e) {
			const { message, path, inner } = e as ValidationError;

			if (path) {
				setError(path, { message: message as string });
			} else {
				inner.forEach((item: any) => {
					setError(item.path, { message: item.message });
				});
			}
		}
	}, [
		conditionValues,
		positiveHandler,
		handleSubmit,
		isEdit,
		setActiveTab,
		setError,
	]);

	const closeThresholdsModal = useCallback(() => {
		setIsOpenThresholdModal(false);
	}, []);

	const openThresholdsModal = useCallback(() => {
		setIsOpenThresholdModal(true);
	}, []);

	const setThresholdsHandler = useCallback((thresholdsValue: IThreshold[]) => {
		setValue('thresholds', thresholdsValue, { shouldValidate: true, shouldDirty: true });

		closeThresholdsModal();
	}, [closeThresholdsModal, setValue]);

	const onBackHandler = useCallback(() => {
		negativeHandler(ETabsIds.checkFor);
	}, [negativeHandler]);

	const onSelectChange = (name: string) => (value: TSelectOption) => {
		setValue(name, value, { shouldValidate: true, shouldDirty: true });
	};

	return (
		<>
			<div className='checkForContainer'>
				<div className='fieldLabel'>
					<span>Threshold source</span>
					<span className='asterisk'>*</span>
				</div>
				<div className='fieldsContainer flex-column'>
					<div className='display-flex flex-row flex-gap_20'>
						<RadioButton
							checked={thresholdSource === EThresholdSource.setManually}
							disabled={isEdit}
							id={EThresholdSource.setManually}
							isError={!!errors?.thresholdSource?.message}
							label='Set manually'
							name={EConditionFields.thresholdSource}
							register={register}
							value={EThresholdSource.setManually}
							withUseForms
						/>
						<RadioButton
							checked={thresholdSource === EThresholdSource.useDefaultForKPIs}
							disabled={isEdit}
							id={EThresholdSource.useDefaultForKPIs}
							isError={!!errors?.thresholdSource?.message}
							label='Use default for KPIs'
							name={EConditionFields.thresholdSource}
							register={register}
							value={EThresholdSource.useDefaultForKPIs}
							withUseForms
						/>
					</div>
					<Button
						onClick={openThresholdsModal}
						secondary
						text={thresholdSource === EThresholdSource.setManually && !isEdit
							? 'Set Thresholds'
							: 'View thresholds'
						}
					/>
					<div className='fieldErrorMessage'>
						{
							errors?.thresholds?.message as string
							|| errors?.thresholds?.message as string
						}
					</div>
				</div>
				<div className='fieldLabel'>
					<span>Apply for</span>
					<span className='asterisk'>*</span>
				</div>
				<div className='fieldsContainer'>
					<RadioButton
						checked={applyFor === EApplyFor.allBuiltCharts}
						disabled={isEdit}
						id={EApplyFor.allBuiltCharts}
						label='All built charts'
						name={EConditionFields.applyFor}
						register={register}
						value={EApplyFor.allBuiltCharts}
						withUseForms
					/>
					<RadioButton
						checked={applyFor === EApplyFor.atLeastOneBuiltChart}
						disabled={isEdit}
						id={EApplyFor.atLeastOneBuiltChart}
						isError={!!errors?.applyFor?.message}
						label='At least 1 built chart'
						name={EConditionFields.applyFor}
						register={register}
						value={EApplyFor.atLeastOneBuiltChart}
						withUseForms
					/>
				</div>
				{errors?.applyFor?.message && <div className='fieldErrorMessage'>{errors?.applyFor?.message}</div>}
				<div className='fieldLabel'>
					<span>Check Every</span>
					<span className='asterisk'>*</span>
				</div>
				<div className={cn('fieldsContainer align-start flex-gap_10', {
					'fieldsContainerError': !!errors?.checkEveryHours?.message
						|| !!errors?.checkEveryMinutes?.message,
				})}>
					<Select
						control={control}
						disabled={isEdit}
						handleChange={onSelectChange(EConditionFields.checkEveryHours)}
						isError={!!errors?.checkEveryHours?.message}
						name={EConditionFields.checkEveryHours}
						options={hoursOptions}
						placeholder='h'
						required
						value={checkEveryHours}
					/>
					<div className='pt-10 mr-5'>
						<span className='fieldLabel'>h</span>
					</div>
					<Select
						control={control}
						disabled={isEdit}
						handleChange={onSelectChange(EConditionFields.checkEveryMinutes)}
						isError={!!errors?.checkEveryMinutes?.message}
						name={EConditionFields.checkEveryMinutes}
						options={minutesOptions}
						placeholder='min'
						required
						value={checkEveryMinutes}
					/>
					<div className='pt-10 mr-5'>
						<span className='fieldLabel'>min</span>
					</div>
				</div>
				<div className='fieldErrorMessage'>
					{
						errors?.checkEveryHours?.message as string
						|| errors?.checkEveryMinutes?.message as string
						|| errors?.checkEveryMinutes?.value?.message as string
					}
				</div>
				<div className='fieldLabel'>
					<span>Evaluation window</span>
					<span className='asterisk'>*</span>
				</div>
				<div className={cn('fieldsContainer align-start flex-gap_10', {
					'fieldsContainerError': !!errors?.checkEveryHours?.message
						|| !!errors?.checkEveryMinutes?.message
						|| !!errors?.evaluationWindowMinutes?.value?.message
						|| errors?.evaluationWindowHours?.message
						|| errors?.evaluationWindowMinutes?.message
						|| !!errors?.evaluationWindowMinutes?.value?.message
					,
				})}>
					<span className='fieldLabel pt-10'>Over the last</span>
					<Select
						control={control}
						disabled={isEdit}
						handleChange={onSelectChange(EConditionFields.evaluationWindowHours)}
						isError={!!errors?.evaluationWindowHours?.message}
						menuIsOpen={true}
						menuPlacement='top'
						menuPortalTarget={document.body}
						name={EConditionFields.evaluationWindowHours}
						options={hoursOptions}
						placeholder='h'
						required
						value={evaluationWindowHours}
					/>
					<div className='pt-10 mr-5'>
						<span className='fieldLabel'>h</span>
					</div>
					<Select
						control={control}
						disabled={isEdit}
						handleChange={onSelectChange(EConditionFields.evaluationWindowMinutes)}
						isError={!!errors?.evaluationWindowMinutes}
						menuPlacement='top'
						name={EConditionFields.evaluationWindowMinutes}
						options={minutesOptions}
						placeholder='min'
						required
						value={evaluationWindowMinutes}
					/>
					<div className='pt-10'>
						<span className='fieldLabel'>min</span>
					</div>
				</div>
				{isLoadingThresholds && <SpinnerOverlay />}
				<div className='fieldErrorMessage'>
					{
						errors?.evaluationWindowHours?.message as string
						|| errors?.evaluationWindowMinutes?.message as string
						|| errors?.evaluationWindowMinutes?.value?.message as string
					}
				</div>
			</div>
			<div className='buttonsContainer'>
				<Button
					onClick={onBackHandler}
					text={negativeBtnText}
					withoutBackground
				/>
				<Button
					disabled={
						Object.keys(conditionValues)
							.some((key: string) => !!errors[key as keyof IKpiAlertSchema])
					}
					onClick={onHandleNext}
					text={positiveBtnText}
				/>
			</div>
			<Modal
				height='550px'
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyForm}
				isOpen={isOpenThresholdModal}
				isScrollableContent
				maxWidth='1100px'
				minWidth='80px'
				onClose={closeThresholdsModal}
				title={`${isEdit ? 'View' : 'Set'} threshold values`}
				width='80%'
			>
				<KpiThresholdsModal
					closeModal={closeThresholdsModal}
					isDirtyForm={isDirtyForm}
					isReadOnly={!!(watch('thresholdSource') === EThresholdSource.useDefaultForKPIs || isEdit)}
					setIsConfirmModal={setIsOpenThresholdModal}
					setIsDirtyForm={setIsDirtyForm}
					setThresholdsHandler={setThresholdsHandler}
					thresholds={values.thresholds || filterThresholdsByKpiIds(thresholds!)}
				/>
			</Modal>
		</>
	);
}

export default memo(AddKpiAlertCondition);
